import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ChevronLeft, Download, Receipt, Loader2, IndianRupee, Calendar, CreditCard, Sparkles,
} from 'lucide-react';

const GATEWAY_LABEL = {
  stripe: 'Stripe',
  paytm: 'Paytm',
  razorpay: 'Razorpay',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return iso.slice(0, 10);
  }
};

export default function MySubscriptionsPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingRef, setDownloadingRef] = useState('');

  useEffect(() => {
    // Wait for auth hydration before deciding to redirect
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/subscriptions/my`, { headers, withCredentials: true });
        if (!cancelled) setPayments(res.data?.payments || []);
      } catch (e) {
        console.error('[subscriptions] fetch failed:', e?.response?.data || e?.message);
        if (!cancelled) setError('Could not load your payment history. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [authLoading, user, token, navigate]);

  const downloadInvoice = async (txnRef) => {
    setDownloadingRef(txnRef);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/subscriptions/${encodeURIComponent(txnRef)}/invoice`, {
        headers,
        responseType: 'blob',
        withCredentials: true,
      });
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      const fileName = match ? match[1] : `invoice-${txnRef}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[subscriptions] invoice download failed:', e?.response?.status, e?.message);
      alert('Could not download invoice. Please try again in a minute.');
    } finally {
      setDownloadingRef('');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header band */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Link to="/dashboard" className="inline-flex items-center gap-1 text-slate-300 hover:text-white text-sm mb-4">
            <ChevronLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Receipt className="w-5 h-5" />
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">My Subscriptions</h1>
              </div>
              <p className="text-slate-300 text-sm">
                Every successful payment comes with an invoice you can download anytime.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/subscription')}
              data-testid="subs-back-to-plans"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Extend / new plan
            </Button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading && (
          <div className="flex items-center justify-center py-16" data-testid="subs-loading">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm" data-testid="subs-error">
            {error}
          </div>
        )}

        {!loading && !error && payments.length === 0 && (
          <Card className="border-dashed border-2 border-slate-200 bg-white" data-testid="subs-empty">
            <CardContent className="p-10 text-center">
              <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-700 mb-1">No paid subscriptions yet</h3>
              <p className="text-sm text-slate-500 mb-4">
                When you buy a Standard plan, all your payment receipts and downloadable invoices will show up here.
              </p>
              <Button onClick={() => navigate('/subscription')} data-testid="subs-empty-cta">
                View plans
              </Button>
            </CardContent>
          </Card>
        )}

        {!loading && !error && payments.length > 0 && (
          <div className="space-y-3" data-testid="subs-list">
            {payments.map((p) => (
              <Card
                key={p.txn_ref}
                className="border-slate-200 hover:border-slate-300 transition-colors"
                data-testid={`subs-row-${p.txn_ref}`}
              >
                <CardContent className="p-4 md:p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    {/* Left: plan + meta */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className="font-semibold text-slate-800 capitalize">
                          {p.plan || 'Standard'} plan
                        </span>
                        <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] uppercase tracking-wider">
                          Paid
                        </Badge>
                        <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px] uppercase tracking-wider">
                          {GATEWAY_LABEL[p.gateway] || p.gateway}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-500 space-y-0.5">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>Paid on {formatDate(p.created_at)}</span>
                          <span className="text-slate-300">·</span>
                          <span>{p.days} day{p.days === 1 ? '' : 's'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 truncate">
                          <CreditCard className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate font-mono">Ref: {p.txn_ref}</span>
                        </div>
                        {p.invoice_id && (
                          <div className="flex items-center gap-1.5">
                            <Receipt className="w-3.5 h-3.5" />
                            <span>Invoice: <span className="font-mono">{p.invoice_id}</span></span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Middle: amount */}
                    <div className="text-right">
                      <div className="flex items-center justify-end gap-0.5 text-slate-900">
                        <IndianRupee className="w-4 h-4" />
                        <span className="text-xl font-bold">
                          {Number(p.amount_inr || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400">Inclusive of all taxes</p>
                    </div>
                  </div>

                  {/* Bottom: actions */}
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadInvoice(p.txn_ref)}
                      disabled={downloadingRef === p.txn_ref}
                      data-testid={`subs-download-invoice-${p.txn_ref}`}
                    >
                      {downloadingRef === p.txn_ref ? (
                        <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Preparing…</>
                      ) : (
                        <><Download className="w-4 h-4 mr-1.5" /> Download invoice</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && !error && payments.length > 0 && (
          <p className="text-[11px] text-slate-400 text-center mt-6 leading-relaxed">
            Invoices are computer-generated tax receipts issued by <span className="font-semibold">MISYOU</span>.<br />
            For any billing question, write to <a href="mailto:grievance@misyou.in" className="underline">grievance@misyou.in</a>.
          </p>
        )}
      </div>
    </div>
  );
}
